import { RectType, SizeType } from '@/util/GeneralType'
import { RoleType } from '@/store/stores/pageStore/common/RoleType'
import { RoleForUserType } from '@/store/stores/loginStore/RoleType'

const SEASON_ROUND_CHECK_IN_TYPE_LIST: Array<`SEASON_ROUND_CHECK_IN_${number}`> = []
// おそらく20ラウンドまで使用することはないと思うが念の為、余裕を持った数値した
const endRoundNumber = 20
for (let i = 1; i <= endRoundNumber; i += 1) {
  SEASON_ROUND_CHECK_IN_TYPE_LIST.push(`SEASON_ROUND_CHECK_IN_${i}`)
}
/**
 * シーズンラウンドチェックインで利用するミッションコードリスト
 */
export const ROUND_CHECK_IN_MISSION_CODE_LIST = [...SEASON_ROUND_CHECK_IN_TYPE_LIST] as const
/**
 * シーズンラウンドチェックインミッションコードの型
 */
export type SeasonRoundCheckInMissionCodeType = typeof ROUND_CHECK_IN_MISSION_CODE_LIST[number]
/**
 * プロフィール登録ミッションで利用するミッションコードリスト
 */
export const REGISTERED_PROFILE_MISSION_CODE_LIST = [
  'REGISTERED_AGE',
  'REGISTERED_AREA',
  'REGISTERED_GENDER',
] as const
/**
 * プロフィール登録ミッションコードの型
 */
export type RegisteredProfileMissionCodeType = typeof REGISTERED_PROFILE_MISSION_CODE_LIST[number]
/**
 * お気に入り登録ミッションで利用するミッションコードリスト
 */
export const REGISTERED_FAVORITE_MISSION_CODE_LIST = [
  'REGISTERED_FAVORITE_PLAYER',
  'REGISTERED_FAVORITE_TEAM',
] as const
/**
 * お気に入り登録ミッションコードの型
 */
export type RegisteredFavoriteMissionCodeType = typeof REGISTERED_FAVORITE_MISSION_CODE_LIST[number]

/**
 * サーキットチェックインで利用する鈴鹿サーキットの情報
 */
export const CHECKED_IN_SUZUKA_CIRCUIT = {
  missionCode: 'CHECKED_IN_SUZUKA_CIRCUIT',
  circuitName: 'Suzuka',
} as const
/**
 * サーキットチェックインで利用するオートポリスの情報
 */
export const CHECKED_IN_AUTOPOLIS = {
  missionCode: 'CHECKED_IN_AUTOPOLIS',
  circuitName: 'Autopolis',
} as const
/**
 * サーキットチェックインで利用するスポーツランドSUGOの情報
 */
export const CHECKED_IN_SPORTLAND_SUGO = {
  missionCode: 'CHECKED_IN_SPORTLAND_SUGO',
  circuitName: 'Sugo',
} as const
/**
 * サーキットチェックインで利用する富士スピードウェイの情報
 */
export const CHECKED_IN_FUJI_SPEEDWAY = {
  missionCode: 'CHECKED_IN_FUJI_SPEEDWAY',
  circuitName: 'Fuji',
} as const
/**
 * サーキットチェックインで利用するモビリティランドもてぎの情報
 */
export const CHECKED_IN_MOBILITY_RESORT_MOTEGI = {
  missionCode: 'CHECKED_IN_MOBILITY_RESORT_MOTEGI',
  circuitName: 'Motegi',
} as const

/**
 * サーキットチェックインで利用するサーキット一覧
 */
export const CHECKED_IN_CIRCUIT_LIST = [
  CHECKED_IN_SUZUKA_CIRCUIT,
  CHECKED_IN_AUTOPOLIS,
  CHECKED_IN_SPORTLAND_SUGO,
  CHECKED_IN_FUJI_SPEEDWAY,
  CHECKED_IN_MOBILITY_RESORT_MOTEGI,
]

/**
 * サーキットチェックインで利用するミッションコード一覧
 */
export const CIRCUIT_CHECK_IN_MISSION_CODE_LIST = [
  ...CHECKED_IN_CIRCUIT_LIST.map((circuit) => circuit.missionCode),
] as const
/**
 * サーキットチェックインミッションコードの型
 */
export type CircuitCheckInMissionCodeType = typeof CIRCUIT_CHECK_IN_MISSION_CODE_LIST[number]

const CHECKED_IN_BONUS_TYPE_LIST: Array<`CHECKED_IN_BONUS_${number}`> = []
// おそらく200箇所イベントチェックインを登録することはないと思うが念の為、余裕を持った数値した
const endCheckInBonusNumber = 200
for (let i = 1; i <= endCheckInBonusNumber; i += 1) {
  CHECKED_IN_BONUS_TYPE_LIST.push(`CHECKED_IN_BONUS_${i}`)
}

/**
 * イベントチェックインで利用するミッションコード一覧
 */
export const EVENT_CHECK_IN_MISSION_CODE_LIST = [...CHECKED_IN_BONUS_TYPE_LIST] as const
/**
 * イベントチェックインミッションコードの型
 */
export type EventCheckInMissionCodeType = typeof EVENT_CHECK_IN_MISSION_CODE_LIST[number]

/**
 * SFgo 会員ランクリストと必要なポイント
 */
const MEMBERS_RANK_NAME_POINTS = [
  { rank: 'bronze', point: 0 },
  { rank: 'silver', point: 1500 },
  { rank: 'gold', point: 3200 },
  { rank: 'platinum', point: 10000 }, // TODO: GOLDより上のランクについてはリリースしないため、ポイントを仮で持たせておく
] as const
export type MembersRankNamePointsType = typeof MEMBERS_RANK_NAME_POINTS[number]['rank'] | undefined
/**
 * 無料プランも含めたSFgo 有料会員ランクの型
 */
export type MembersRankNameType = typeof MEMBERS_RANK_NAME_POINTS[number]['rank'] | 'free'

/**
 * アプリ内課金定期購入商品 製品ID
 * 「.test」がついているものはテスト用の製品IDでトライアル機能リリース以降は使用しない
 */
const IN_APP_PURCHASE_SUBSC_PRODUCT_ID = {
  IOS: {
    ANNUAL: 'net.superformula.sfgo.subsc.annual',
    MONTHLY: 'net.superformula.sfgo.subsc.monthly',
    ANNUAL_TEST: 'net.superformula.sfgo.subsc.annual.test',
    MONTHLY_TEST: 'net.superformula.sfgo.subsc.monthly.test',
  },
  ANDROID: {
    ANNUAL: 'net.superformula.sfgo.subsc.annual',
    MONTHLY: 'net.superformula.sfgo.subsc.monthly',
  },
} as const

/**
 * アプリ内課金定期購入商品 プラングループID
 * ANDROIDの場合は製品IDとプラングループIDが異なるので定義
 * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/3377659981
 */
const IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID = {
  IOS: {
    ANNUAL: IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.ANNUAL,
    MONTHLY: IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.MONTHLY,
    ANNUAL_TEST: IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.ANNUAL_TEST,
    MONTHLY_TEST: IN_APP_PURCHASE_SUBSC_PRODUCT_ID.IOS.MONTHLY_TEST,
  },
  ANDROID: {
    ANNUAL: 'sfgo-annual-subscription',
    MONTHLY: 'sfgo-monthly-subscription',
  },
} as const
export type InAppPurchasePlanGroupType =
  | typeof IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS[keyof typeof IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS]
  | typeof IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.ANDROID[keyof typeof IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.ANDROID]

/**
 * アプリ内課金トライアルプラン プラングループID
 * 「.test」がついているものはテスト用の製品IDでトライアル機能リリース以降は使用しない
 * @see https://pitchbase.atlassian.net/wiki/spaces/SFGO/pages/3379429607/SFgo
 */
const IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID = {
  IOS: {
    ANNUAL: 'net.superformula.sfgo.subsc.annual.trial',
    MONTHLY: 'net.superformula.sfgo.subsc.monthly.trial',
    ANNUAL_TEST: 'net.superformula.sfgo.subsc.annual.trial.test',
    MONTHLY_TEST: 'net.superformula.sfgo.subsc.monthly.trial.test',
  },
  ANDROID: {
    ANNUAL: 'sfgo-annual-subscription-trial',
    MONTHLY: 'sfgo-monthly-subscription-trial',
  },
} as const
export type InAppPurchaseTrialPlanGroupIdType =
  | typeof IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS[keyof typeof IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS]
  | typeof IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.ANDROID[keyof typeof IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.ANDROID]

/**
 * 年額プラン/年額プラントライアルのプラングループID一覧
 */
export const ANNUAL_PLAN_GROUP_IDS = [
  // 定期購入
  IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.ANNUAL,
  IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.IOS.ANNUAL_TEST,
  IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID.ANDROID.ANNUAL,
  // トライアル
  IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.ANNUAL,
  IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.IOS.ANNUAL_TEST,
  IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID.ANDROID.ANNUAL,
] as const

/**
 * SFgoユーザーのロール
 */
const SFGO_ROLES: RoleType[] = [
  'team-user',
  'team-engineer',
  'driver',
  'ambassador',
  'guest',
  'sfgo-dev-supporter',
  'adviser',
  'special-supporter',
  'jrp-staff',
]

/**
 * ユーザー種別表現用のロール
 * 開発サポーターというロールは存在しないため、取り除く
 */
const SFGO_ROLES_FOR_USER_TYPE = SFGO_ROLES.filter(
  (role) => role !== 'sfgo-dev-supporter',
) as RoleForUserType[]

/**
 * ハイライト/ハイライトコメント通報フォームの項目ID
 */
const REPORT_FORM_OPTION_IDS = process.env.VUE_APP_GOOGLE_FORM_REPORT_ENTRIES.split(',')

/**
 * SUPER FORMULA公式サイトのURL
 */
const SUPER_FORMULA_OFFICIAL_URL = 'https://superformula.net/sf3/'
/**
 * SUPER FORMULA公式サイトの旧URL
 */
const SUPER_FORMULA_OFFICIAL_OLD_URL = 'https://superformula.net/sf2/'
/**
 * SUPER FORMULA ライブタイミングサイトのURL
 */
const SUPER_FORMULA_LIVE_TIMING_URL = 'http://superformula.racelive.jp/'
/**
 * SUPER FORMULA ECサイトのURL
 */
const SUPER_FORMULA_EC_SITE_URL = 'https://ec.superformula.net/shop/'
/**
 * XサイトのURL
 */
const X_URL = 'https://x.com/'
/**
 * INSTAGRAMサイトのURL
 */
const INSTAGRAM = 'https://www.instagram.com/'
/**
 * YOUTUBEサイトのURL
 */
const YOUTUBE = 'https://www.youtube.com/'

/**
 * 動画エクスポートのウォータマークの画像をbase64化した文字列
 * NOTE: 動画エクスポートする際に、useMovieExport>readFileToBase64でソースファイル内の画像をbase64化しようとしているが、
 * iPhoneでは「SecurityError: The operation is insecure.」になってしまうため、ウォーターマークで使用する画像をあらかじめbase64化してこの変数に格納している。
 */
export const MOVIE_EXPORT_WATER_MARK_FOR_BASE64 =
  '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'

/**
 * 動画エクスポートの際の出力動画サイズ
 */
export const MOVIE_EXPORT_MOVIE_SIZE: SizeType = {
  width: 960,
  height: 540,
}

/**
 * 動画エクスポートの際のウォータマークの表示位置とサイズ
 */
export const MOVIE_EXPORT_VIEW_ITEM_RECT: RectType = {
  origin: {
    x: 826,
    y: 30,
  },
  size: {
    width: 104,
    height: 32,
  },
}

export default {
  /**
   * 会員登録タイプ
   */
  MEMBER_TYPE: {
    FREE: 'free',
    PAID: 'paid',
  } as const,
  /**
   * SFgoで使用するロール
   */
  SFGO_ROLES,
  /**
   * ユーザー種別表現用のロール
   */
  SFGO_ROLES_FOR_USER_TYPE,
  /**
   * ユーザー種別表現用のラベル
   */
  SFGO_USER_TYPE_LABELS: {
    'team-user': 'Team',
    'team-engineer': 'Engineer',
    driver: 'Driver',
    ambassador: 'Ambassador',
    guest: 'Guest',
    'special-supporter': 'Special\nSupporter',
    'jrp-staff': 'Staff',
    adviser: 'Advisor',
    'paid-user': 'User',
    'free-user': 'User',
  },
  /**
   * SFgo 会員ランクリストと必要なポイント
   */
  MEMBERS_RANK_NAME_POINTS,
  /**
   * アプリ内課金定期購入商品 製品ID
   */
  IN_APP_PURCHASE_SUBSC_PRODUCT_ID,
  /**
   * アプリ内課金定期購入商品 プラングループID
   */
  IN_APP_PURCHASE_SUBSC_PLAN_GROUP_ID,
  /**
   * アプリ内課金トライアルプラン プラングループID
   */
  IN_APP_PURCHASE_SUBSC_TRIAL_PLAN_GROUP_ID,
  /**
   * 視聴画質に応じて、コンテンツ情報APIのどの解像度の動画を再生するかの対応表
   */
  RESOLUTION: {
    STANDARD: ['QUARTER_FULLHD'],
    HIGH_QUALITY: ['FULLHD', 'HD'],
  },
  /**
   * HOMEのカウントダウン サーキット画像 URL取得
   */
  HOME_COUNTDOWN_IMAGE_PATH: {
    FUJI_SPEEDWAY: 'assets/img/HomePage/course_layout/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/HomePage/course_layout/suzuka.svg',
    AUTOPOLIS: 'assets/img/HomePage/course_layout/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/HomePage/course_layout/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/HomePage/course_layout/motegi.svg',
    DEFAULT: 'assets/img/HomePage/icon_race.svg',
  },
  /**
   * サーキット画像URL取得
   */
  IMAGE_PATH: {
    FUJI_SPEEDWAY: 'assets/img/RaceListPage/course_layout/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/RaceListPage/course_layout/suzuka.svg',
    AUTOPOLIS: 'assets/img/RaceListPage/course_layout/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/RaceListPage/course_layout/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/RaceListPage/course_layout/motegi.svg',
  },
  /**
   * サーキット簡易MAP URL取得
   */
  GPS_CIRCUIT_MAP_PATH: {
    FUJI_SPEEDWAY: 'assets/img/RaceVideoPage/course_layout/small/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/RaceVideoPage/course_layout/small/suzuka.svg',
    AUTOPOLIS: 'assets/img/RaceVideoPage/course_layout/small/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/RaceVideoPage/course_layout/small/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/RaceVideoPage/course_layout/small/motegi.svg',
  },
  /**
   * サーキット詳細MAP-BirdsEye URL取得
   */
  GPS_CIRCUIT_BIRDSEYE_MAP_PATH: {
    FUJI_SPEEDWAY: 'assets/img/RaceVideoPage/course_layout/birdsEye/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/RaceVideoPage/course_layout/birdsEye/suzuka.svg',
    AUTOPOLIS: 'assets/img/RaceVideoPage/course_layout/birdsEye/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/RaceVideoPage/course_layout/birdsEye/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/RaceVideoPage/course_layout/birdsEye/motegi.svg',
  },
  /**
   * サーキット詳細MAP-BATTLE URL取得
   */
  GPS_CIRCUIT_BATTLE_MAP_PATH: {
    FUJI_SPEEDWAY: 'assets/img/RaceVideoPage/course_layout/battle/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/RaceVideoPage/course_layout/battle/suzuka.svg',
    AUTOPOLIS: 'assets/img/RaceVideoPage/course_layout/battle/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/RaceVideoPage/course_layout/battle/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/RaceVideoPage/course_layout/battle/motegi.svg',
  },
  /**
   * サーキット詳細MAP-FOCUS URL取得
   */
  GPS_CIRCUIT_FOCUS_MAP_PATH: {
    FUJI_SPEEDWAY: 'assets/img/RaceVideoPage/course_layout/focus/fuji.svg',
    SUZUKA_CIRCUIT: 'assets/img/RaceVideoPage/course_layout/focus/suzuka.svg',
    AUTOPOLIS: 'assets/img/RaceVideoPage/course_layout/focus/auto_police.svg',
    SPORTSLAND_SUGO: 'assets/img/RaceVideoPage/course_layout/focus/sugo.svg',
    TWIN_RING_MOTEGI: 'assets/img/RaceVideoPage/course_layout/focus/motegi.svg',
  },
  /**
   * サーキット詳細MAP 縮尺ゲージ URL取得
   */
  GPS_CIRCUIT_SCALE_GAUGE_WIDTH: {
    FUJI_SPEEDWAY: 29,
    SUZUKA_CIRCUIT: 26,
    AUTOPOLIS: 43,
    SPORTSLAND_SUGO: 56,
    TWIN_RING_MOTEGI: 49,
  },
  /**
   * GPS スロットリング設定
   */
  GPS_THROTTLING: {
    THRESHOLD: 2,
    NO_ANIMATION_GAP_TIME_MS: 10000,
    CAR_DISABLED_THRESHOLD: 5,
    CAR_HIDE_THRESHOLD: 50,
  },
  /**
   * GPS詳細 モード別倍率
   */
  GPS_MAP_MAGNIFICATION: {
    BIRDSEYE: 1,
    BATTLE: 10,
    FOCUS: 30,
  },
  /**
   * サーキット簡易MAP GPS 座標
   */
  GPS_CIRCUIT_MAP_GPS: {
    UNKNOWN: {
      root: {
        lat: 0,
        lng: 0,
      },
      end: {
        lat: 0,
        lng: 0,
      },
    },
    FUJI_SPEEDWAY: {
      root: {
        lat: 35.37767,
        lng: 138.91436,
      },
      end: {
        lat: 35.3635,
        lng: 138.939355,
      },
    },
    SUZUKA_CIRCUIT: {
      root: {
        lat: 34.85195,
        lng: 136.51805,
      },
      end: {
        lat: 34.83551,
        lng: 136.54725,
      },
    },
    AUTOPOLIS: {
      root: {
        lat: 33.04206,
        lng: 130.9636,
      },
      end: {
        lat: 33.03164,
        lng: 130.982,
      },
    },
    SPORTSLAND_SUGO: {
      root: {
        lat: 38.14487,
        lng: 140.76887,
      },
      end: {
        lat: 38.13609,
        lng: 140.78435,
      },
    },
    TWIN_RING_MOTEGI: {
      root: {
        lat: 36.53715,
        lng: 140.22367,
      },
      end: {
        lat: 36.5278,
        lng: 140.24032,
      },
    },
  },
  /**
   * FL-UX バックエンドAPIのドメイン名
   */
  FLUX_DOMAIN: 'sfgo.jp',
  /**
   * 配信開始日時の調整時間(ミリ秒)
   */
  RECORDING_START_ADJUST_TIME: 5000,
  /**
   * ライブ配信中のサーキットモードの場合、ライブタイミング、テレメトリーデータ・GPSを現在時間からどのくらい遅延させるかの値(ミリ秒)
   */
  ADJUST_TRACK_DATE_TIME: 2000,
  /**
   * ライブ配信が開始されたと判定するまでの時間マージン(ミリ秒)。
   * ContentsInfoDocument#recordingStartTime の時間から、この定数に設定された時間が経過したら、ライブ配信開始したと判定する。
   * recordingStartTime が記録された瞬間は、まだ映像データが生成されておらず、動画再生ができない場合があるため。
   */
  LIVE_START_MARGIN: 10000,
  /**
   * ハイライト情報のポーリング間隔(ミリ秒)
   */
  HIGHLIGHT_POLING_INTERVAL: 3000,
  /**
   * 無線情報のポーリング間隔(ミリ秒)
   */
  RADIO_POLING_INTERVAL: 10000,
  /**
   * レース関連情報のポーリング間隔(ミリ秒)
   */
  RACE_DATA_POLING_INTERVAL: 30000,
  /**
   * 組織情報のポーリング間隔(ミリ秒)
   */
  ORGANIZATION_POLING_INTERVAL: 5000,
  /**
   * 契約情報のポーリング間隔(ミリ秒)
   */
  CONTRACT_INFO_POLING_INTERVAL: 3000,
  /**
   * DynamoDBの認証で許容できる最大の時差(ミリ秒)
   */
  DYNAMO_DB_MAX_DIFF_TIME: 900000,
  /**
   * 選手情報を設定するためのチームの名称
   */
  SUPER_FORMULA_DRIVERS_TEAM_NAME: 'SUPER FORMULA DRIVERS',
  /**
   * 常に選手リストに表示する選手の場合の背番号に含まれる文字列
   */
  CAR_NO_ALWAYS_SHOW: 'ALWAYS_SHOW',
  /**
   * 外部リンクリスト
   */
  EXTERNAL_LINKS: {
    // 新機能追加のお知らせページ。新機能追加のお知らせを表示する場合、都度更新する。
    NEW_RELEASE: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}release/16674/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}release/16674/#googtrans(ja|en)`,
    },
    ABOUT_SFGO: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}sfgo/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}sfgo/#googtrans(ja|en)`,
    },
    INFORMATION: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}information/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}information/#googtrans(ja|en)`,
    },
    DRIVER_STANDINGS: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}driver_taxonomy/2025/?standings`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}driver_taxonomy/2025/?standings/#googtrans(ja|en)`,
    },
    TEAM_STANDINGS: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}team_taxonomy/2025/?standings`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}team_taxonomy/2025/?standings/#googtrans(ja|en)`,
    },
    LIVE_TIMING: {
      JA: SUPER_FORMULA_LIVE_TIMING_URL,
      EN: SUPER_FORMULA_LIVE_TIMING_URL,
    },
    ABOUT_SF: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/#googtrans(ja|en)`,
    },
    DRIVER_TAXONOMY: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}driver_taxonomy/2025/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}driver_taxonomy/2025/#googtrans(ja|en)`,
    },
    TEAM_TAXONOMY: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}team_taxonomy/2025/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}team_taxonomy/2025/#googtrans(ja|en)`,
    },
    ABOUT_MACHINE: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_machine/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_machine/#googtrans(ja|en)`,
    },
    RACE_TAXONOMY: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}race_taxonomy/2025/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}race_taxonomy/2025/#googtrans(ja|en)`,
    },
    ABOUT_HISTORY: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_history/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_history/#googtrans(ja|en)`,
    },
    ABOUT_DRIVER: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_driver/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}about_sf/about_driver/#googtrans(ja|en)`,
    },
    ABOUT_MEMBER_RANK: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}release/9961/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}release/9961/#googtrans(ja|en)`,
    },
    ABOUT_AWARD: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}release/9750/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}release/9750/#googtrans(ja|en)`,
    },
    GOOGLE_INQUIRY_FORM: 'https://forms.gle/2QccFEhr8bcKomRo9',
    GOOGLE_MP: {
      JA: 'https://www.google.co.jp/maps/?hl=ja',
      EN: 'https://www.google.co.jp/maps/?hl=en',
    },
    /**
     * ハイライト/ハイライトコメント通報フォームのURL
     */
    REPORT_FORM_URL: {
      BASE_URL: process.env.VUE_APP_GOOGLE_FORM_REPORT_URL,
      OPTION: {
        REPORTER_EMAIL: `entry.${REPORT_FORM_OPTION_IDS[0]}`,
        REPORTER_ID: `entry.${REPORT_FORM_OPTION_IDS[1]}`,
        VIOLATING_USER_NICKNAME: `entry.${REPORT_FORM_OPTION_IDS[2]}`,
        VIOLATING_USER_ID: `entry.${REPORT_FORM_OPTION_IDS[3]}`,
        HIGHLIGHT_TIME: `entry.${REPORT_FORM_OPTION_IDS[4]}`,
        HIGHLIGHT_URL: `entry.${REPORT_FORM_OPTION_IDS[5]}`,
        COMMENT: `entry.${REPORT_FORM_OPTION_IDS[6]}`,
        HIGHLIGHT_ID: `entry.${REPORT_FORM_OPTION_IDS[7]}`,
        COMMENT_ID: `entry.${REPORT_FORM_OPTION_IDS[8]}`,
      },
    },
    X: {
      HOME: X_URL,
      SUPER_FORMULA: `${X_URL}SUPER_FORMULA`,
      FUJI: `${X_URL}fswpress`,
      SUZUKA: `${X_URL}suzuka_event`,
      AUTOPOLIS: `${X_URL}autopolis2005`,
      MOTEGI: `${X_URL}motegi_official`,
    },
    INSTAGRAM: {
      HOME: INSTAGRAM,
      OFFICIAL: `${INSTAGRAM}superformula_official`,
    },
    YOUTUBE: {
      OFFICIAL: `${YOUTUBE}@superformula_official`,
    },
    PRIVACY_POLICY: `${SUPER_FORMULA_OFFICIAL_OLD_URL}sfgoprivacypolicy`,
    TRADING_RULE: `${SUPER_FORMULA_OFFICIAL_OLD_URL}sfgotradingrule`,
    TERMS: `${SUPER_FORMULA_OFFICIAL_OLD_URL}sfgoterms`,
    EC_SITE: {
      REGISTRATION: `${SUPER_FORMULA_EC_SITE_URL}customers/sign_up`,
      TOP: SUPER_FORMULA_EC_SITE_URL,
    },
    IN_APP_PURCHASE: {
      SUBSCRIPTION_MANAGEMENT: {
        IOS: 'https://apps.apple.com/account/subscriptions',
        ANDROID: 'https://play.google.com/store/account/subscriptions/',
      },
      HISTORY: {
        IOS: 'https://reportaproblem.apple.com/',
        ANDROID: 'https://play.google.com/store/account/orderhistory/',
      },
      CHANGE: {
        IOS: {
          JA: 'https://support.apple.com/ja-jp/118448',
          EN: 'https://support.apple.com/en-us/118448',
        },
      },
      CANCEL: {
        IOS: {
          JA: 'https://support.apple.com/ja-jp/118428',
          EN: 'https://support.apple.com/en-us/118428',
        },
      },
      BILLING: {
        IOS: 'https://apps.apple.com/account/billing',
        ANDROID: 'https://play.google.com/store/paymentmethods',
      },
      SUPPORT: {
        IOS: 'https://getsupport.apple.com/',
      },
    },
    OAUTH2: {
      SIGN_IN: {
        IOS: {
          JA: 'https://support.apple.com/ja-jp/102571',
          EN: 'https://support.apple.com/en-us/102571',
        },
      },
    },
    ABOUT_EC_SITE: {
      JA: `${SUPER_FORMULA_OFFICIAL_URL}release/15579/`,
      EN: `${SUPER_FORMULA_OFFICIAL_URL}release/15579/#googtrans(ja|en)`,
    },
  },
  /**
   * ハイライト、ハイライトコメント最大入力文字数
   */
  MAX_HIGHLIGHT_COMMENT_LENGTH: 280,
  /**
   * ニックネーム最大入力文字数
   */
  MAX_DISPLAY_NAME_LENGTH: 24,
  /**
   * 性別回答リスト
   */
  USER_GENDER_LIST: [
    {
      name: {
        ja: '男性',
        en: 'Male',
      },
      id: 'MALE',
    },
    {
      name: {
        ja: '女性',
        en: 'Female',
      },
      id: 'FEMALE',
    },
    {
      name: {
        ja: '回答しない',
        en: 'Unanswered',
      },
      id: 'NO',
    },
  ],
  /**
   * 国リスト
   */
  USER_COUNTRY_LIST: [
    {
      name: {
        ja: 'アフガニスタン',
        en: 'Afghanistan',
      },
      id: 'AF',
    },
    {
      name: {
        ja: 'アルバニア',
        en: 'Albania',
      },
      id: 'AL',
    },
    {
      name: {
        ja: 'アルジェリア',
        en: 'Algeria',
      },
      id: 'DZ',
    },
    {
      name: {
        ja: '米国領サモア',
        en: 'US Samoa',
      },
      id: 'AS',
    },
    {
      name: {
        ja: 'アンドラ',
        en: 'Andorra',
      },
      id: 'AD',
    },
    {
      name: {
        ja: 'アンゴラ',
        en: 'Angola',
      },
      id: 'AO',
    },
    {
      name: {
        ja: '南極',
        en: 'Antarctic',
      },
      id: 'AQ',
    },
    {
      name: {
        ja: 'アンティグア バーブーダ',
        en: 'Antigua and Barbuda',
      },
      id: 'AG',
    },
    {
      name: {
        ja: 'アルゼンチン',
        en: 'Argentina',
      },
      id: 'AR',
    },
    {
      name: {
        ja: 'アルメニア',
        en: 'Armenia',
      },
      id: 'AM',
    },
    {
      name: {
        ja: 'アルバ',
        en: 'Aruba',
      },
      id: 'AW',
    },
    {
      name: {
        ja: 'オーストラリア',
        en: 'Australia',
      },
      id: 'AU',
    },
    {
      name: {
        ja: 'オーストリア',
        en: 'Austria',
      },
      id: 'AT',
    },
    {
      name: {
        ja: 'アゼルバイジャン',
        en: 'Azerbaijan',
      },
      id: 'AZ',
    },
    {
      name: {
        ja: 'バハマ',
        en: 'Bahamas',
      },
      id: 'BS',
    },
    {
      name: {
        ja: 'バーレーン',
        en: 'Bahrain',
      },
      id: 'BH',
    },
    {
      name: {
        ja: 'バングラディシュ',
        en: 'Bangladesh',
      },
      id: 'BD',
    },
    {
      name: {
        ja: 'バルバドス',
        en: 'Barbados',
      },
      id: 'BB',
    },
    {
      name: {
        ja: 'べラルーシ',
        en: 'Belarus',
      },
      id: 'BY',
    },
    {
      name: {
        ja: 'ベルギー',
        en: 'Belgium',
      },
      id: 'BE',
    },
    {
      name: {
        ja: 'ベリーズ',
        en: 'Belize',
      },
      id: 'BZ',
    },
    {
      name: {
        ja: 'ベリーズ',
        en: 'Benin',
      },
      id: 'BJ',
    },
    {
      name: {
        ja: 'バミューダ諸島',
        en: 'Bermuda',
      },
      id: 'BM',
    },
    {
      name: {
        ja: 'ブータン',
        en: 'Bhutan',
      },
      id: 'BT',
    },
    {
      name: {
        ja: 'ボリビア',
        en: 'Bolivia',
      },
      id: 'BO',
    },
    {
      name: {
        ja: 'ボスニア ヘルツェゴビナ',
        en: 'Bosnia-Herzegovina',
      },
      id: 'BA',
    },
    {
      name: {
        ja: 'ボツワナ',
        en: 'Botswana',
      },
      id: 'BW',
    },
    {
      name: {
        ja: 'ブーベ島',
        en: 'Bouvet Island',
      },
      id: 'BV',
    },
    {
      name: {
        ja: 'ブラジル',
        en: 'Brazil',
      },
      id: 'BR',
    },
    {
      name: {
        ja: 'イギリス領インド洋領域',
        en: 'British Indian Ocean Territory',
      },
      id: 'IO',
    },
    {
      name: {
        ja: 'ブルネイ ダルサラーム',
        en: 'Brunei Darussalam',
      },
      id: 'BN',
    },
    {
      name: {
        ja: 'ブルガリア',
        en: 'Bulgaria',
      },
      id: 'BG',
    },
    {
      name: {
        ja: 'ブルキナ ファソ',
        en: 'Burkina Faso',
      },
      id: 'BF',
    },
    {
      name: {
        ja: 'ブルンジ',
        en: 'Burundi',
      },
      id: 'BI',
    },
    {
      name: {
        ja: 'カンボジア',
        en: 'Cambodia',
      },
      id: 'KH',
    },
    {
      name: {
        ja: 'カメルーン',
        en: 'Cameroon',
      },
      id: 'CM',
    },
    {
      name: {
        ja: 'カナダ',
        en: 'Canada',
      },
      id: 'CA',
    },
    {
      name: {
        ja: 'カーボ ベルデ',
        en: 'Cape Verde',
      },
      id: 'CV',
    },
    {
      name: {
        ja: 'ケイマン諸島',
        en: 'Cayman Islands',
      },
      id: 'KY',
    },
    {
      name: {
        ja: '中央アフリカ共和国',
        en: 'Central African Republic',
      },
      id: 'CF',
    },
    {
      name: {
        ja: 'チャド',
        en: 'Chad',
      },
      id: 'TD',
    },
    {
      name: {
        ja: 'チリ',
        en: 'Chile',
      },
      id: 'CL',
    },
    {
      name: {
        ja: '中国',
        en: 'China',
      },
      id: 'CN',
    },
    {
      name: {
        ja: 'クリスマス島',
        en: 'Christmas island',
      },
      id: 'CX',
    },
    {
      name: {
        ja: 'ココス (キーリング) 諸島',
        en: 'Cocos (key ring) Islands',
      },
      id: 'CC',
    },
    {
      name: {
        ja: 'コロンビア',
        en: 'Columbia',
      },
      id: 'CO',
    },
    {
      name: {
        ja: 'コモロ',
        en: 'CoComoroslumbia',
      },
      id: 'KM',
    },
    {
      name: {
        ja: 'コンゴ',
        en: 'Congo',
      },
      id: 'CG',
    },
    {
      name: {
        ja: 'コンゴ民主共和国',
        en: 'Democratic Republic of the Congo',
      },
      id: 'CD',
    },
    {
      name: {
        ja: 'クック諸島',
        en: 'Cook Islands',
      },
      id: 'CK',
    },
    {
      name: {
        ja: 'コスタリカ',
        en: 'Costa Rica',
      },
      id: 'CR',
    },
    {
      name: {
        ja: 'コートジボアール',
        en: "Cote d'Ivoire",
      },
      id: 'CI',
    },
    {
      name: {
        ja: 'クロアチア',
        en: 'Croatia',
      },
      id: 'HR',
    },
    {
      name: {
        ja: 'キューバ',
        en: 'Cuba',
      },
      id: 'CU',
    },
    {
      name: {
        ja: 'キプロス',
        en: 'Cyprus',
      },
      id: 'CY',
    },
    {
      name: {
        ja: 'チェコ共和国',
        en: 'Czech Republic',
      },
      id: 'CZ',
    },
    {
      name: {
        ja: 'デンマーク',
        en: 'Denmark',
      },
      id: 'DK',
    },
    {
      name: {
        ja: 'ジブチ',
        en: 'Djibouti',
      },
      id: 'DJ',
    },
    {
      name: {
        ja: 'ドミニカ',
        en: 'Dominica',
      },
      id: 'DM',
    },
    {
      name: {
        ja: 'ドミニカ共和国',
        en: 'Dominican Republic',
      },
      id: 'DO',
    },
    {
      name: {
        ja: 'エクアドル',
        en: 'Ecuador',
      },
      id: 'EC',
    },
    {
      name: {
        ja: 'エジプト',
        en: 'Egypt',
      },
      id: 'EG',
    },
    {
      name: {
        ja: 'エルサルバドル',
        en: 'El salvador',
      },
      id: 'SV',
    },
    {
      name: {
        ja: '赤道ギニア',
        en: 'Equatorial Guinea',
      },
      id: 'GQ',
    },
    {
      name: {
        ja: 'エリトリア',
        en: 'Eritrea',
      },
      id: 'ER',
    },
    {
      name: {
        ja: 'エストニア',
        en: 'Estonia',
      },
      id: 'EE',
    },
    {
      name: {
        ja: 'エチオピア',
        en: 'Ethiopia',
      },
      id: 'ET',
    },
    {
      name: {
        ja: 'フォークランド (マルビナス) 諸島',
        en: 'Falkland (Malvinas) Islands',
      },
      id: 'FK',
    },
    {
      name: {
        ja: 'フェロー諸島',
        en: 'Faroe Islands',
      },
      id: 'FO',
    },
    {
      name: {
        ja: 'フィジー',
        en: 'Fiji',
      },
      id: 'FJ',
    },
    {
      name: {
        ja: 'フィンランド',
        en: 'Finland',
      },
      id: 'FI',
    },
    {
      name: {
        ja: 'フランス',
        en: 'France',
      },
      id: 'FR',
    },
    {
      name: {
        ja: 'フランス領ギアナ',
        en: 'French Guiana',
      },
      id: 'GF',
    },
    {
      name: {
        ja: 'フランス領ポリネシア',
        en: 'French Polynesia',
      },
      id: 'PF',
    },
    {
      name: {
        ja: 'フランス南方領',
        en: 'French Southern and Antarctic',
      },
      id: 'TF',
    },
    {
      name: {
        ja: 'ガボン',
        en: 'Gabon',
      },
      id: 'GA',
    },
    {
      name: {
        ja: 'ガンビア',
        en: 'Gambia',
      },
      id: 'GM',
    },
    {
      name: {
        ja: 'グルジア',
        en: 'Georgia',
      },
      id: 'GE',
    },
    {
      name: {
        ja: 'ドイツ',
        en: 'Germany',
      },
      id: 'DE',
    },
    {
      name: {
        ja: 'ガーナ',
        en: 'Ghana',
      },
      id: 'GH',
    },
    {
      name: {
        ja: 'ジブラルタル',
        en: 'Gibraltar',
      },
      id: 'GI',
    },
    {
      name: {
        ja: 'ギリシャ',
        en: 'Greece',
      },
      id: 'GR',
    },
    {
      name: {
        ja: 'グリーンランド',
        en: 'Greenland',
      },
      id: 'GL',
    },
    {
      name: {
        ja: 'グレナダ',
        en: 'Grenada',
      },
      id: 'GD',
    },
    {
      name: {
        ja: 'グアドループ島',
        en: 'Guadeloupe',
      },
      id: 'GP',
    },
    {
      name: {
        ja: 'グアム',
        en: 'Guam',
      },
      id: 'GU',
    },
    {
      name: {
        ja: 'グアテマラ',
        en: 'Guatemala',
      },
      id: 'GT',
    },
    {
      name: {
        ja: 'ギニア',
        en: 'Guinea',
      },
      id: 'GN',
    },
    {
      name: {
        ja: 'ギニアビサウ',
        en: 'Guinea-Bissau',
      },
      id: 'GW',
    },
    {
      name: {
        ja: 'ガイアナ',
        en: 'Guyana',
      },
      id: 'GY',
    },
    {
      name: {
        ja: 'ハイチ',
        en: 'Haiti',
      },
      id: 'HT',
    },
    {
      name: {
        ja: 'ハード島およびマクドナルド諸島',
        en: 'Heard Island and McDonald Islands',
      },
      id: 'HM',
    },
    {
      name: {
        ja: 'ホンジュラス',
        en: 'Honduras',
      },
      id: 'HN',
    },
    {
      name: {
        ja: '香港',
        en: 'Hong Kong',
      },
      id: 'HK',
    },
    {
      name: {
        ja: 'ハンガリー',
        en: 'Hungary',
      },
      id: 'HU',
    },
    {
      name: {
        ja: 'アイスランド',
        en: 'Iceland',
      },
      id: 'IS',
    },
    {
      name: {
        ja: 'インド',
        en: 'India',
      },
      id: 'IN',
    },
    {
      name: {
        ja: 'インドネシア',
        en: 'Indonesia',
      },
      id: 'ID',
    },
    {
      name: {
        ja: 'イラン イスラム共和国',
        en: 'Iran Islamic Republic',
      },
      id: 'IR',
    },
    {
      name: {
        ja: 'イラク',
        en: 'Iraq',
      },
      id: 'IQ',
    },
    {
      name: {
        ja: 'アイルランド',
        en: 'Ireland',
      },
      id: 'IE',
    },
    {
      name: {
        ja: 'イスラエル',
        en: 'Israel',
      },
      id: 'IL',
    },
    {
      name: {
        ja: 'イタリア',
        en: 'Italy',
      },
      id: 'IT',
    },
    {
      name: {
        ja: 'ジャマイカ',
        en: 'Jamaica',
      },
      id: 'JM',
    },
    {
      name: {
        ja: '日本',
        en: 'Japan',
      },
      id: 'JP',
    },
    {
      name: {
        ja: 'ヨルダン',
        en: 'Jordan',
      },
      id: 'JO',
    },
    {
      name: {
        ja: 'カザフスタン',
        en: 'Kazakhstan',
      },
      id: 'KZ',
    },
    {
      name: {
        ja: 'ケニア',
        en: 'Kenya',
      },
      id: 'KE',
    },
    {
      name: {
        ja: 'キリバス',
        en: 'Kiribati',
      },
      id: 'KI',
    },
    {
      name: {
        ja: '朝鮮民主主義人民共和国 (北朝鮮）',
        en: "Democratic People's Republic of Korea (North Korea)",
      },
      id: 'KP',
    },
    {
      name: {
        ja: '大韓民国 (韓国)',
        en: 'Republic of Korea (Korea)',
      },
      id: 'KR',
    },
    {
      name: {
        ja: 'クウェート',
        en: 'Kuwait',
      },
      id: 'KW',
    },
    {
      name: {
        ja: 'キルギスタン',
        en: 'Kyrgyzstan',
      },
      id: 'KG',
    },
    {
      name: {
        ja: 'ラオス人民民主共和国',
        en: "Lao People's Democratic Republic",
      },
      id: 'LA',
    },
    {
      name: {
        ja: 'ラトビア',
        en: 'Latvia',
      },
      id: 'LV',
    },
    {
      name: {
        ja: 'レバノン',
        en: 'Lebanon',
      },
      id: 'LB',
    },
    {
      name: {
        ja: 'レソト',
        en: 'Lesotho',
      },
      id: 'LS',
    },
    {
      name: {
        ja: 'リベリア',
        en: 'Liberia',
      },
      id: 'LR',
    },
    {
      name: {
        ja: 'リビア アラブ ジャマーヒリーヤ国',
        en: 'Libya Arab Jamahiriya',
      },
      id: 'LY',
    },
    {
      name: {
        ja: 'リヒテンシュタイン',
        en: 'Liechtenstein',
      },
      id: 'LI',
    },
    {
      name: {
        ja: 'リトアニア',
        en: 'Lithuania',
      },
      id: 'LT',
    },
    {
      name: {
        ja: 'ルクセンブルク',
        en: 'Luxembourg',
      },
      id: 'LU',
    },
    {
      name: {
        ja: 'マカオ',
        en: 'Macau',
      },
      id: 'MO',
    },
    {
      name: {
        ja: 'マケドニア旧ユーゴスラビア共和国',
        en: 'Former Republic of Macedonia Yugoslavia',
      },
      id: 'MK',
    },
    {
      name: {
        ja: 'マダガスカル',
        en: 'Madagascar',
      },
      id: 'MG',
    },
    {
      name: {
        ja: 'マラウイ',
        en: 'Malawi',
      },
      id: 'MW',
    },
    {
      name: {
        ja: 'マレーシア',
        en: 'Malaysia',
      },
      id: 'MY',
    },
    {
      name: {
        ja: 'モルディブ',
        en: 'Maldives',
      },
      id: 'MV',
    },
    {
      name: {
        ja: 'マリ',
        en: 'Mali',
      },
      id: 'ML',
    },
    {
      name: {
        ja: 'マルタ',
        en: 'Malta',
      },
      id: 'MT',
    },
    {
      name: {
        ja: 'マーシャル諸島',
        en: 'Marshall Islands',
      },
      id: 'MH',
    },
    {
      name: {
        ja: 'マルティニーク',
        en: 'Martinique',
      },
      id: 'MQ',
    },
    {
      name: {
        ja: 'モーリタニア',
        en: 'Mauritania',
      },
      id: 'MR',
    },
    {
      name: {
        ja: 'モーリシャス',
        en: 'Mauritius',
      },
      id: 'MU',
    },
    {
      name: {
        ja: 'マイヨット',
        en: 'My yacht',
      },
      id: 'YT',
    },
    {
      name: {
        ja: 'メキシコ',
        en: 'Mexico',
      },
      id: 'MX',
    },
    {
      name: {
        ja: 'ミクロネシア連邦',
        en: 'Federated States of Micronesia',
      },
      id: 'FM',
    },
    {
      name: {
        ja: 'モルドバ共和国',
        en: 'Republic of Moldova',
      },
      id: 'MD',
    },
    {
      name: {
        ja: 'モナコ',
        en: 'Monaco',
      },
      id: 'MC',
    },
    {
      name: {
        ja: 'モンゴル',
        en: 'Mongolia',
      },
      id: 'MN',
    },
    {
      name: {
        ja: 'モントセラト',
        en: 'Montserrat',
      },
      id: 'MS',
    },
    {
      name: {
        ja: 'モロッコ',
        en: 'Morocco',
      },
      id: 'MA',
    },
    {
      name: {
        ja: 'モザンビーク',
        en: 'Mozambique',
      },
      id: 'MZ',
    },
    {
      name: {
        ja: 'ミャンマー',
        en: 'Myanmar',
      },
      id: 'MM',
    },
    {
      name: {
        ja: 'ナミビア',
        en: 'Namibia',
      },
      id: 'NA',
    },
    {
      name: {
        ja: 'ナウル',
        en: 'Nauru',
      },
      id: 'NR',
    },
    {
      name: {
        ja: 'ネパール',
        en: 'Nepal',
      },
      id: 'NP',
    },
    {
      name: {
        ja: 'オランダ',
        en: 'Netherlands',
      },
      id: 'NL',
    },
    {
      name: {
        ja: 'オランダ領アンティル',
        en: 'Netherlands Antilles',
      },
      id: 'AN',
    },
    {
      name: {
        ja: 'ニューカレドニア',
        en: 'New Caledonia',
      },
      id: 'NC',
    },
    {
      name: {
        ja: 'ニュージーランド',
        en: 'new Zealand',
      },
      id: 'NZ',
    },
    {
      name: {
        ja: 'ニカラグア',
        en: 'Nicaragua',
      },
      id: 'NI',
    },
    {
      name: {
        ja: 'ニジェール',
        en: 'Niger',
      },
      id: 'NE',
    },
    {
      name: {
        ja: 'ナイジェリア',
        en: 'Nigeria',
      },
      id: 'NG',
    },
    {
      name: {
        ja: 'ニウエ',
        en: 'Niue',
      },
      id: 'NU',
    },
    {
      name: {
        ja: 'ノーフォーク島',
        en: 'Norfolk Island',
      },
      id: 'NF',
    },
    {
      name: {
        ja: '北マリアナ諸島',
        en: 'Northern Mariana Islands',
      },
      id: 'MP',
    },
    {
      name: {
        ja: 'ノルウェー',
        en: 'Norway',
      },
      id: 'NO',
    },
    {
      name: {
        ja: 'オマーン',
        en: 'Oman',
      },
      id: 'OM',
    },
    {
      name: {
        ja: 'パキスタン',
        en: 'Pakistan',
      },
      id: 'PK',
    },
    {
      name: {
        ja: 'パラオ',
        en: 'Palau',
      },
      id: 'PW',
    },
    {
      name: {
        ja: 'パレスチナ自治政府',
        en: 'Palestinian Authority',
      },
      id: 'PS',
    },
    {
      name: {
        ja: 'パナマ',
        en: 'Panama',
      },
      id: 'PA',
    },
    {
      name: {
        ja: 'パプアニューギニア',
        en: 'Papua New Guinea',
      },
      id: 'PG',
    },
    {
      name: {
        ja: 'パラグアイ',
        en: 'Paraguay',
      },
      id: 'PY',
    },
    {
      name: {
        ja: 'ペルー',
        en: 'Peru',
      },
      id: 'PE',
    },
    {
      name: {
        ja: 'フィリピン',
        en: 'Philippines',
      },
      id: 'PH',
    },
    {
      name: {
        ja: 'ピトケアン諸島',
        en: 'Pitcairn Islands',
      },
      id: 'PN',
    },
    {
      name: {
        ja: 'ポーランド',
        en: 'Poland',
      },
      id: 'PL',
    },
    {
      name: {
        ja: 'ポルトガル',
        en: 'Portugal',
      },
      id: 'PT',
    },
    {
      name: {
        ja: 'プエルトリコ',
        en: 'Puerto Rico',
      },
      id: 'PR',
    },
    {
      name: {
        ja: 'カタール',
        en: 'Qatar',
      },
      id: 'QA',
    },
    {
      name: {
        ja: 'レユニオン島',
        en: 'Reunion Island',
      },
      id: 'RE',
    },
    {
      name: {
        ja: 'ルーマニア',
        en: 'Romania',
      },
      id: 'RO',
    },
    {
      name: {
        ja: 'ロシア連邦',
        en: 'Russian Federation',
      },
      id: 'RU',
    },
    {
      name: {
        ja: 'ルワンダ',
        en: 'Rwanda',
      },
      id: 'RW',
    },
    {
      name: {
        ja: 'セントヘレナ',
        en: 'St. Helena',
      },
      id: 'SH',
    },
    {
      name: {
        ja: 'セントクリストファー ネーヴィス',
        en: 'Saint Kitts and Nevis',
      },
      id: 'KN',
    },
    {
      name: {
        ja: 'セントルシア',
        en: 'Saint Lucia',
      },
      id: 'LC',
    },
    {
      name: {
        ja: 'サンピエール ミクロン',
        en: 'Saint Pierre Micron',
      },
      id: 'PM',
    },
    {
      name: {
        ja: 'セントビンセントおよびグレナディーン諸島',
        en: 'Saint Vincent and the Grenadines',
      },
      id: 'VC',
    },
    {
      name: {
        ja: 'サモア',
        en: 'Samoa',
      },
      id: 'WS',
    },
    {
      name: {
        ja: 'サンマリノ',
        en: 'San Marino',
      },
      id: 'SM',
    },
    {
      name: {
        ja: 'サントメ プリンシペ',
        en: 'Sao Tome Principe',
      },
      id: 'ST',
    },
    {
      name: {
        ja: 'サウジアラビア',
        en: 'Saudi Arabia',
      },
      id: 'SA',
    },
    {
      name: {
        ja: 'セネガル',
        en: 'Senegal',
      },
      id: 'SN',
    },
    {
      name: {
        ja: 'セルビア モンテネグロ',
        en: 'Serbia and Montenegro',
      },
      id: 'CS',
    },
    {
      name: {
        ja: 'セーシェル',
        en: 'Seychelles',
      },
      id: 'SC',
    },
    {
      name: {
        ja: 'シエラレオネ',
        en: 'Sierra Leone',
      },
      id: 'SL',
    },
    {
      name: {
        ja: 'シンガポール',
        en: 'Singapore',
      },
      id: 'SG',
    },
    {
      name: {
        ja: 'スロバキア',
        en: 'Slovakia',
      },
      id: 'SK',
    },
    {
      name: {
        ja: 'スロベニア',
        en: 'Slovenia',
      },
      id: 'SI',
    },
    {
      name: {
        ja: 'ソロモン諸島',
        en: 'Solomon Islands',
      },
      id: 'SB',
    },
    {
      name: {
        ja: 'ソマリア',
        en: 'Somalia',
      },
      id: 'SO',
    },
    {
      name: {
        ja: '南アフリカ',
        en: 'South Africa',
      },
      id: 'ZA',
    },
    {
      name: {
        ja: '南ジョージア南サンドイッチ諸島',
        en: 'South Georgia South Sandwich Islands',
      },
      id: 'GS',
    },
    {
      name: {
        ja: 'スペイン',
        en: 'Spain',
      },
      id: 'ES',
    },
    {
      name: {
        ja: 'スリランカ',
        en: 'Sri Lanka',
      },
      id: 'LK',
    },
    {
      name: {
        ja: 'スーダン',
        en: 'Sudan',
      },
      id: 'SD',
    },
    {
      name: {
        ja: 'スリナム',
        en: 'Suriname',
      },
      id: 'SR',
    },
    {
      name: {
        ja: 'スバールバル ヤンマイエン諸島',
        en: 'Svalbard and Jan Mayen Islands',
      },
      id: 'SJ',
    },
    {
      name: {
        ja: 'スワジランド',
        en: 'Swaziland',
      },
      id: 'SZ',
    },
    {
      name: {
        ja: 'スウェーデン',
        en: 'Sweden',
      },
      id: 'SE',
    },
    {
      name: {
        ja: 'スイス',
        en: 'Switzerland',
      },
      id: 'CH',
    },
    {
      name: {
        ja: 'シリア アラブ共和国',
        en: 'Syria Arab Republic',
      },
      id: 'SY',
    },
    {
      name: {
        ja: '台湾省/中華民国',
        en: 'Taiwan Province / Republic of China',
      },
      id: 'TW',
    },
    {
      name: {
        ja: 'タジキスタン',
        en: 'Tajikistan',
      },
      id: 'TJ',
    },
    {
      name: {
        ja: 'タンザニア連合共和国',
        en: 'United Republic of Tanzania',
      },
      id: 'TZ',
    },
    {
      name: {
        ja: 'タイ',
        en: 'Thailand',
      },
      id: 'TH',
    },
    {
      name: {
        ja: '東ティモール',
        en: 'East Timor',
      },
      id: 'TL',
    },
    {
      name: {
        ja: 'トーゴ',
        en: 'Togo',
      },
      id: 'TG',
    },
    {
      name: {
        ja: 'トケラウ',
        en: 'Tokelau',
      },
      id: 'TK',
    },
    {
      name: {
        ja: 'トンガ',
        en: 'Tonga',
      },
      id: 'TO',
    },
    {
      name: {
        ja: 'トリニダード トバゴ',
        en: 'Republic of Trinidad and Tobago',
      },
      id: 'TT',
    },
    {
      name: {
        ja: 'チュニジア',
        en: 'Tunisia',
      },
      id: 'TN',
    },
    {
      name: {
        ja: 'トルコ',
        en: 'Turkey',
      },
      id: 'TR',
    },
    {
      name: {
        ja: 'トルクメニスタン',
        en: 'Turkmenistan',
      },
      id: 'TM',
    },
    {
      name: {
        ja: 'タークス カイコス諸島',
        en: 'Turks and Caicos Islands',
      },
      id: 'TC',
    },
    {
      name: {
        ja: 'ツバル',
        en: 'Tuvalu',
      },
      id: 'TV',
    },
    {
      name: {
        ja: 'ウガンダ',
        en: 'Uganda',
      },
      id: 'UG',
    },
    {
      name: {
        ja: 'ウクライナ',
        en: 'Ukraine',
      },
      id: 'UA',
    },
    {
      name: {
        ja: 'アラブ首長国連邦',
        en: 'United Arab Emirates',
      },
      id: 'AE',
    },
    {
      name: {
        ja: 'イギリス',
        en: 'England',
      },
      id: 'GB',
    },
    {
      name: {
        ja: 'アメリカ',
        en: 'America',
      },
      id: 'US',
    },
    {
      name: {
        ja: 'アメリカ合衆国外諸島',
        en: 'Outer United States Islands',
      },
      id: 'UM',
    },
    {
      name: {
        ja: 'ウルグアイ',
        en: 'Uruguay',
      },
      id: 'UY',
    },
    {
      name: {
        ja: 'ウズベキスタン',
        en: 'Uzbekistan',
      },
      id: 'UZ',
    },
    {
      name: {
        ja: 'バヌアツ',
        en: 'Vanuatu',
      },
      id: 'VU',
    },
    {
      name: {
        ja: 'ベネズエラ',
        en: 'Venezuela',
      },
      id: 'VE',
    },
    {
      name: {
        ja: 'ベトナム',
        en: 'Vietnam',
      },
      id: 'VN',
    },
    {
      name: {
        ja: 'イギリス領ヴァージン諸島',
        en: 'British Virgin Islands',
      },
      id: 'VG',
    },
    {
      name: {
        ja: 'アメリカ領ヴァージン諸島',
        en: 'American Virgin Islands',
      },
      id: 'VI',
    },
    {
      name: {
        ja: 'ウォリス フツナ諸島',
        en: 'Wallis and Futuna Islands',
      },
      id: 'WF',
    },
    {
      name: {
        ja: '西サハラ',
        en: 'Western Sahara',
      },
      id: 'EH',
    },
    {
      name: {
        ja: 'イエメン',
        en: 'Yemen',
      },
      id: 'YE',
    },
    {
      name: {
        ja: 'ザンビア',
        en: 'Zambia',
      },
      id: 'ZM',
    },
    {
      name: {
        ja: 'ジンバブエ',
        en: 'Zimbabwe',
      },
      id: 'ZW',
    },
  ],

  /**
   * 都道府県リスト
   */
  USER_PREFECTURES_LIST: [
    {
      name: {
        ja: '北海道',
        en: 'Hokkaido',
      },
      id: '01',
    },
    {
      name: {
        ja: '青森県',
        en: 'Aomori',
      },
      id: '02',
    },
    {
      name: {
        ja: '岩手県',
        en: 'Iwate',
      },
      id: '03',
    },
    {
      name: {
        ja: '宮城県',
        en: 'Miyagi',
      },
      id: '04',
    },
    {
      name: {
        ja: '秋田県',
        en: 'Akita',
      },
      id: '05',
    },
    {
      name: {
        ja: '山形県',
        en: 'Yamagata',
      },
      id: '06',
    },
    {
      name: {
        ja: '福島県',
        en: 'Fukushima',
      },
      id: '07',
    },
    {
      name: {
        ja: '茨城県',
        en: 'Ibaraki',
      },
      id: '08',
    },
    {
      name: {
        ja: '栃木県',
        en: 'Tochigi',
      },
      id: '09',
    },
    {
      name: {
        ja: '群馬県',
        en: 'Gunma',
      },
      id: '10',
    },
    {
      name: {
        ja: '埼玉県',
        en: 'Saitama',
      },
      id: '11',
    },
    {
      name: {
        ja: '千葉県',
        en: 'Chiba',
      },
      id: '12',
    },
    {
      name: {
        ja: '東京都',
        en: 'Tokyo',
      },
      id: '13',
    },
    {
      name: {
        ja: '神奈川県',
        en: 'Kanagawa',
      },
      id: '14',
    },
    {
      name: {
        ja: '新潟県',
        en: 'Niigata',
      },
      id: '15',
    },
    {
      name: {
        ja: '富山県',
        en: 'Toyama',
      },
      id: '16',
    },
    {
      name: {
        ja: '石川県',
        en: 'Ishikawa',
      },
      id: '17',
    },
    {
      name: {
        ja: '福井県',
        en: 'Fukui',
      },
      id: '18',
    },
    {
      name: {
        ja: '山梨県',
        en: 'Yamanashi',
      },
      id: '19',
    },
    {
      name: {
        ja: '長野県',
        en: 'Nagano',
      },
      id: '20',
    },
    {
      name: {
        ja: '岐阜県',
        en: 'Gifu',
      },
      id: '21',
    },
    {
      name: {
        ja: '静岡県',
        en: 'Shizuoka',
      },
      id: '22',
    },
    {
      name: {
        ja: '愛知県',
        en: 'Aichi',
      },
      id: '23',
    },
    {
      name: {
        ja: '三重県',
        en: 'Mie',
      },
      id: '24',
    },
    {
      name: {
        ja: '滋賀県',
        en: 'Shiga',
      },
      id: '25',
    },
    {
      name: {
        ja: '京都府',
        en: 'Kyoto',
      },
      id: '26',
    },
    {
      name: {
        ja: '大阪府',
        en: 'Osaka',
      },
      id: '27',
    },
    {
      name: {
        ja: '兵庫県',
        en: 'Hyogo',
      },
      id: '28',
    },
    {
      name: {
        ja: '奈良県',
        en: 'Nara',
      },
      id: '29',
    },
    {
      name: {
        ja: '和歌山県',
        en: 'Wakayama',
      },
      id: '30',
    },
    {
      name: {
        ja: '鳥取県',
        en: 'Tottori',
      },
      id: '31',
    },
    {
      name: {
        ja: '島根県',
        en: 'Shimane',
      },
      id: '32',
    },
    {
      name: {
        ja: '岡山県',
        en: 'Okayama',
      },
      id: '33',
    },
    {
      name: {
        ja: '広島県',
        en: 'Hiroshima',
      },
      id: '34',
    },
    {
      name: {
        ja: '山口県',
        en: 'Yamaguchi',
      },
      id: '35',
    },
    {
      name: {
        ja: '徳島県',
        en: 'Tokushima',
      },
      id: '36',
    },
    {
      name: {
        ja: '香川県',
        en: 'Kagawa',
      },
      id: '37',
    },
    {
      name: {
        ja: '愛媛県',
        en: 'Ehime',
      },
      id: '38',
    },
    {
      name: {
        ja: '高知県',
        en: 'Kochi',
      },
      id: '39',
    },
    {
      name: {
        ja: '福岡県',
        en: 'Fukuoka',
      },
      id: '40',
    },
    {
      name: {
        ja: '佐賀県',
        en: 'Saga',
      },
      id: '41',
    },
    {
      name: {
        ja: '長崎県',
        en: 'Nagasaki',
      },
      id: '42',
    },
    {
      name: {
        ja: '熊本県',
        en: 'Kumamoto',
      },
      id: '43',
    },
    {
      name: {
        ja: '大分県',
        en: 'Oita',
      },
      id: '44',
    },
    {
      name: {
        ja: '宮崎県',
        en: 'Miyazaki',
      },
      id: '45',
    },
    {
      name: {
        ja: '鹿児島県',
        en: 'Kagoshima',
      },
      id: '46',
    },
    {
      name: {
        ja: '沖縄県',
        en: 'Okinawa',
      },
      id: '47',
    },
  ],
}
